import React, { Component } from 'react';
import withStyles from '@mui/styles/withStyles';
import TextField from './TextField';
import Paper from '@mui/material/Paper';
import Downshift from 'downshift';
import cx from 'classnames';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import { CloseIcon, SearchIcon } from '../IconIndex';
import ValidationPlaceholder from './ValidationPlaceholder';
import { getDownshiftInputProps } from '../../util/functions';
import { Popper } from '@mui/material';

class AutoComplete extends Component {
    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this.state = {
            focused: false,
            options: null, // list of searchable options, typically [{label, value|key},]
            searchTerm: '', // what user types in
            searchField: this.props.searchField ?? 'label', // the property of "options" to match against the searchTerm (typically "label")
            propsValue: undefined // compare prev props to new props
        };
    }

    static getDerivedStateFromProps(newProps, oldState) {
        const { name, form, value, searchField = 'label', options = [] } = newProps;
        const { propsValue } = oldState;
        let formValue = value;
        // if form is present, use that value instead
        if ((form && !form.loading) || !form) {
            formValue = form?.getField(name);
            if ('0' === formValue && !options.find(obj => '0' === obj.value)) {
                // if we have an ID=0 but no matching option, clear it
                formValue = '';
            }
        }
        if (formValue !== propsValue) {
            // new value from parent
            const newValue = (form ? formValue : value) || '';
            const newSearchTerm = (newValue ? newValue[searchField] || newValue : null) || '';
            const searchTerm = options.find(opt => newSearchTerm === (opt.key || opt.value));
            return {
                propsValue: formValue,
                searchField,
                options,
                searchTerm: searchTerm ? searchTerm[searchField] : newValue
            };
        }
        if (JSON.stringify(options) !== JSON.stringify(oldState.options)) {
            // new options from parent
            return { options };
        }
        return null;
    }

    onSearchFieldFocus = e => {
        if (!!this.inputRef) this.inputRef.focus();
        if (this.state.focused) return;

        this.setState({ focused: true });
    };

    onSearchFieldBlur = () => {
        const { searchTerm, focused } = this.state;
        if (!focused) return;

        const { name, form } = this.props;
        if (form && form.getField(name) !== searchTerm) {
            this.selectItem(this.getMatchingItem());
        } else {
            this.setState({ focused: false });
        }
    };

    onSearchFieldRef = input => {
        this.inputRef = input;
    };

    onSearchFieldKeyPress = (e, highlightedIndex, currentOptions) => {
        if (['Enter', 'Tab'].includes(e.key)) {
            if (highlightedIndex === null) {
                const match = this.getMatchingItem();
                if (match) this.selectItem(match);
                else e.preventDefault();
            } else {
                const nearest = currentOptions[highlightedIndex];
                if (nearest) this.selectItem(nearest);
                else e.preventDefault();
            }
        }
    };

    onSearchFieldChange = e => {
        this.onSearchFieldFocus();
        this.setState({ searchTerm: e.target.value });
    };

    selectItem = item => {
        const { clearOnSelect } = this.props;
        const { searchField } = this.state;
        const key = item ? item['key'] || item['value'] || item : null;
        const label = item ? item[searchField] : '';
        const { onChange, name, form } = this.props;
        if (item?.disabled !== true) {
            this.setState({ searchTerm: clearOnSelect ? '' : label, focused: false }, () => {
                if (form) form.setField({ [name]: key });
                if (onChange) onChange(item || {});
            });
        }
    };

    getMatchingItem = () => {
        const { options, searchTerm, searchField } = this.state;
        const upperSearchTerm = searchTerm.toUpperCase();
        return options.find(x => String(x?.[searchField] ?? '').toUpperCase() === upperSearchTerm);
    };

    render() {
        const props = {
            ...this.props,
            onItemSelected: this.selectItem,
            onSearchFieldChange: this.onSearchFieldChange,
            onSearchFieldKeyPress: this.onSearchFieldKeyPress,
            onSearchFieldFocus: this.onSearchFieldFocus,
            onSearchFieldBlur: this.onSearchFieldBlur,
            onSearchFieldRef: this.onSearchFieldRef
        };
        return <Downshift>{downshiftData => this.renderContainer(props, downshiftData)}</Downshift>;
    }

    renderContainer = (props, data) => {
        const { searchTerm, focused, options, searchField } = this.state;
        const { form, name, classes, className, onItemSelected, onSearchFieldRef, limit = 10 } = props;
        const { maxHeight } = this.props;
        const { highlightedIndex } = data;
        const validOptions =
            options && Array.isArray(options)
                ? options.filter(
                      item => !searchTerm || item[searchField].toLowerCase().includes(searchTerm.toLowerCase())
                  )
                : [];
        const s = validOptions.length - limit === 1;
        return (
            <div className={cx(classes.root, className)}>
                <ValidationPlaceholder form={form} name={name}>
                    <div style={{ padding: 0, position: 'relative', width: '100%' }}>
                        <SearchField
                            {...props}
                            ref={onSearchFieldRef}
                            currentOptions={validOptions}
                            data={data}
                            searchTerm={searchTerm}
                            highlightedIndex={highlightedIndex}
                        />
                        <Popper
                            open={focused}
                            anchorEl={this.inputRef?.parentElement}
                            className={classes.popper}
                            placement="bottom-start"
                            style={{ width: this.input?.parentElement?.clientWidth }}
                        >
                            <Paper className={classes.itemPaper} square style={{ maxHeight }}>
                                {validOptions.length > 0 ? (
                                    validOptions
                                        .filter((item, index) => index < limit)
                                        .map((item, index) => (
                                            <SearchResult
                                                key={index}
                                                {...props}
                                                data={data}
                                                selectItem={onItemSelected}
                                                item={item}
                                                index={index}
                                                searchField={searchField}
                                            />
                                        ))
                                ) : (
                                    <div className={classes.noResult}>
                                        <span className={classes.itemLabel}>No results, try a different search?</span>
                                    </div>
                                )}
                                {validOptions.length > limit && (
                                    <div className={classes.noResult}>
                                        <span className={classes.itemLabel}>
                                            There {!s ? 'are' : 'is'} {validOptions.length - limit} more result
                                            {!s ? 's' : ''}, refine your search?
                                        </span>
                                    </div>
                                )}
                            </Paper>
                        </Popper>
                    </div>
                </ValidationPlaceholder>
            </div>
        );
    };
}

const SearchField = React.forwardRef(
    (
        {
            open,
            label,
            placeholder,
            onSearchFieldChange,
            onSearchFieldKeyPress,
            onSearchFieldFocus,
            onSearchFieldBlur,
            onItemSelected,
            currentOptions,
            data,
            searchTerm,
            highlightedIndex,
            disabled,
            required,
            form,
            name
        },
        onSearchFieldRef
    ) => {
        const downshiftInputProps = getDownshiftInputProps(data.getInputProps);
        const dLabelProps = data.getLabelProps();
        const dInputProps = downshiftInputProps({
            placeholder,
            onFocus: onSearchFieldFocus,
            onBlur: onSearchFieldBlur,
            onChange: onSearchFieldChange,
            onKeyDown: e => onSearchFieldKeyPress(e, highlightedIndex, currentOptions),
            onClick: e => onSearchFieldFocus(e),
            value: searchTerm
        });

        // This is to shut up linting errors because it's case-insensitive
        const otherProps = {
            required,
            InputLabelProps: dLabelProps,
            InputProps: {
                ...dInputProps,
                tabIndex: open ? 0 : -1,
                disableUnderline: true,
                inputRef: onSearchFieldRef,
                disabled: disabled,
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon style={{ fontSize: 16, color: '#413BBE' }} />
                    </InputAdornment>
                ),
                endAdornment: searchTerm && !disabled && (
                    <InputAdornment position="end">
                        <span title="Remove">
                            <CloseIcon
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: 'grey',
                                    padding: 1,
                                    fontSize: 12,
                                    borderRadius: 36,
                                    color: 'white'
                                }}
                                onClick={() => {
                                    onSearchFieldChange({ target: { value: '' } });
                                    onItemSelected(null);
                                }}
                            />
                        </span>
                    </InputAdornment>
                )
            }
        };
        return <TextField name="searchTerm" label={label} fullWidth {...otherProps} />;
    }
);

const SearchResult = ({ classes, data, selectItem, item, index, searchField }) => {
    const key = item.key || item.value;
    const label = item[searchField];
    const itemProps = data.getItemProps({ item: key });
    return (
        <MenuItem
            key={key}
            {...itemProps}
            className={cx(classes.item)}
            selected={data.highlightedIndex === index}
            disabled={item.disabled}
            onClick={() => (item.disabled ? null : selectItem(item))}
        >
            <span className={classes.itemLabel}>{label}</span>
        </MenuItem>
    );
};

const styles = ({ spacing, palette, transitions, typography }) => ({
    root: {
        position: 'relative',
        flex: '1 1 auto'
    },
    popper: {
        zIndex: 1300,
        clipPath: 'inset(0px -10px -10px -10px)', // no shadow at top
        minWidth: 360,
        '&[data-popper-placement=top-start]': {
            clipPath: 'inset(-10px -10px 0px -10px)' // no shadow at bottom
        }
    },
    noResult: {
        color: '#757575',
        padding: 16,
        '&:not(:first-child)': {
            borderTop: '1px solid #d5d5d5',
            padding: '8px 16px 0',
            textAlign: 'right'
        }
    },
    iconContainer: {
        alignSelf: 'center'
    },
    icon: {
        fontSize: 16,
        color: palette.action.active
    },
    itemPaper: {
        border: '1px solid #d5d5d5',
        borderRadius: 4,
        padding: '8px 0',
        maxHeight: '50vh',
        overflow: 'auto',
        minWidth: 250
    },
    item: {
        padding: '6px 16px',
        display: 'flex'
    },
    itemLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '0.875rem'
    }
});

export default withStyles(styles)(AutoComplete);
